import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, ArrowLeft, Send, Image as ImageIcon, X, Heart, Smile, Sparkles, HandHeart, Hand, MoreVertical, Edit2, Trash2, ChevronDown, Pin, PinOff, Plus, Settings, Lock, Search, ChevronUp, ChevronDown as ChevronDownIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useAuth } from '../AuthContext';
import { useToast } from '../hooks/use-toast';

const Community = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { toast } = useToast();
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewPostModal, setShowNewPostModal] = useState(false);
  const [newPostContent, setNewPostContent] = useState('');
  const [newPostImages, setNewPostImages] = useState([]);
  const [selectedPostChannel, setSelectedPostChannel] = useState(null); // For "All" channel posting
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedComments, setExpandedComments] = useState({});
  const [comments, setComments] = useState({});
  const [newComment, setNewComment] = useState({});
  const [editingPost, setEditingPost] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [hasAgreedToRules, setHasAgreedToRules] = useState(false);
  const fileInputRef = useRef(null);
  
  // Check if user is admin (either through user.is_admin flag or admin_token presence)
  const isAdmin = user?.is_admin || !!localStorage.getItem('admin_token');
  // Channel management states (admin only)
  const [showChannelModal, setShowChannelModal] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [channelForm, setChannelForm] = useState({
    name: '',
    description: '',
    emoji: '💬',
    channel_type: 'general',
    access_level: 'free',
    is_read_only: false
  });
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  // Post menu state
  const [openPostMenu, setOpenPostMenu] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
  
  // Debug: Log API_BASE_URL on component mount
  useEffect(() => {
    console.log('Community component loaded. API_BASE_URL:', API_BASE_URL);
  }, []);

  // Check if user has agreed to community rules
  useEffect(() => {
    const agreed = localStorage.getItem(`community_rules_agreed_${user?.id}`);
    if (agreed === 'true') {
      setHasAgreedToRules(true);
    } else {
      setShowRulesModal(true);
    }
  }, [user]);

  // Reaction emojis mapping
  const REACTIONS = {
    love: { emoji: '💖', label: 'Love' },
    celebrate: { emoji: '🙌', label: 'Celebrate' },
    inspire: { emoji: '🌟', label: 'Inspire' },
    sparkle: { emoji: '✨', label: 'Sparkle' },
    grateful: { emoji: '🙏', label: 'Grateful' }
  };

  useEffect(() => {
    loadCommunityChannels();
  }, []);

  useEffect(() => {
    if (selectedChannel) {
      if (selectedChannel.id === 'all') {
        loadAllPosts();
      } else {
        loadChannelPosts(selectedChannel.id);
      }
    }
  }, [selectedChannel]);

  const loadCommunityChannels = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/community/channels`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const realChannels = data.channels || [];
        
        // Remove duplicates by ID (in case of any caching issues)
        const uniqueChannels = realChannels.filter((channel, index, self) =>
          index === self.findIndex((c) => c.id === channel.id)
        );
        
        // Create "All" channel at the beginning
        const allChannel = {
          id: 'all',
          name: 'All Posts',
          emoji: '✨',
          description: 'See posts from all channels',
          is_read_only: false
        };
        
        console.log('Loaded channels:', uniqueChannels.length, uniqueChannels.map(c => c.name));
        
        setChannels([allChannel, ...uniqueChannels]);
        setSelectedChannel(allChannel); // Select "All" by default
      }
    } catch (error) {
      console.error('Error loading channels:', error);
      toast({
        title: "Error",
        description: "Failed to load community channels",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAllPosts = async () => {
    try {
      // Use the new optimized endpoint that fetches all posts in one API call
      const response = await fetch(`${API_BASE_URL}/api/community/all-posts?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setPosts(data.posts || []);
      }
    } catch (error) {
      console.error('Error loading all posts:', error);
    }
  };

  const loadChannelPosts = async (channelId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/community/channels/${channelId}/posts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setPosts(data.posts || []);
      }
    } catch (error) {
      console.error('Error loading posts:', error);
    }
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setIsSubmitting(true);
    const uploadedUrls = [];

    try {
      for (const file of files) {
        // Validate file type (images and PDFs)
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
        if (!validTypes.includes(file.type)) {
          toast({
            title: "Invalid File Type",
            description: `${file.name} is not supported. Please upload images (JPG, PNG, GIF, WebP) or PDFs.`,
            variant: "destructive"
          });
          continue;
        }

        // Validate file size (max 10MB)
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
          toast({
            title: "File Too Large",
            description: `${file.name} exceeds 10MB limit.`,
            variant: "destructive"
          });
          continue;
        }

        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${API_BASE_URL}/api/community/upload-file`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (response.ok) {
          const data = await response.json();
          // Store both URL and file metadata
          const fileObj = {
            url: `${API_BASE_URL}${data.url}`,
            type: file.type,
            name: file.name,
            originalUrl: data.url
          };
          console.log('File uploaded successfully:', fileObj);
          uploadedUrls.push(fileObj);
        } else {
          const error = await response.json();
          toast({
            title: "Upload Failed",
            description: error.detail || `Failed to upload ${file.name}`,
            variant: "destructive"
          });
        }
      }

      setNewPostImages([...newPostImages, ...uploadedUrls]);
    } catch (error) {
      console.error('Error uploading files:', error);
      toast({
        title: "Error",
        description: "Failed to upload files",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const removeImage = (index) => {
    setNewPostImages(newPostImages.filter((_, i) => i !== index));
  };

  const handleCreatePost = async () => {
    if (!newPostContent.trim()) {
      toast({
        title: "Error",
        description: "Post content cannot be empty",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    // Default to General Discussions if no channel selected from "All"
    let targetChannelId;
    if (selectedChannel.id === 'all') {
      if (selectedPostChannel) {
        targetChannelId = selectedPostChannel;
      } else {
        // Default to General Discussions
        const generalDiscussions = channels.find(c => c.name === '💬 General Discussions');
        targetChannelId = generalDiscussions?.id || channels.filter(c => c.id !== 'all' && !c.is_read_only)[0]?.id;
      }
    } else {
      targetChannelId = selectedChannel.id;
    }

    try {
      // Extract URLs for backend (use originalUrl if available, otherwise use the URL itself)
      const imageUrls = newPostImages.map(file => 
        typeof file === 'object' ? file.originalUrl : file
      );

      const response = await fetch(`${API_BASE_URL}/api/community/channels/${targetChannelId}/posts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          channel_id: targetChannelId,
          content: newPostContent,
          images: imageUrls
        })
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Post created successfully!"
        });
        setNewPostContent('');
        setNewPostImages([]);
        setSelectedPostChannel(null);
        setShowNewPostModal(false);
        
        if (selectedChannel.id === 'all') {
          loadAllPosts();
        } else {
          loadChannelPosts(selectedChannel.id);
        }
      } else {
        const error = await response.json();
        toast({
          title: "Error",
          description: error.detail || "Failed to create post",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error creating post:', error);
      toast({
        title: "Error",
        description: "Failed to create post",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditPost = async (postId) => {
    if (!editContent.trim()) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/community/posts/${postId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          content: editContent
        })
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Post updated successfully!"
        });
        setEditingPost(null);
        setEditContent('');
        if (selectedChannel.id === 'all') {
          loadAllPosts();
        } else {
          loadChannelPosts(selectedChannel.id);
        }
      } else {
        const error = await response.json();
        toast({
          title: "Error",
          description: error.detail || "Failed to update post",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error updating post:', error);
      toast({
        title: "Error",
        description: "Failed to update post",
        variant: "destructive"
      });
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/community/posts/${postId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Post deleted successfully!"
        });
        if (selectedChannel.id === 'all') {
          loadAllPosts();
        } else {
          loadChannelPosts(selectedChannel.id);
        }
      } else {
        const error = await response.json();
        toast({
          title: "Error",
          description: error.detail || "Failed to delete post",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };

  const handlePinPost = async (postId, isPinned) => {
    try {
      const url = `${API_BASE_URL}/api/community/posts/${postId}/pin?pin=${!isPinned}`;
      console.log('🔵 Pinning post:', { postId, isPinned, newPinState: !isPinned, url });
      
      // Use user endpoint for pin/unpin (works with admin user token)
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('🔵 Pin response:', response.status, response.statusText);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Pin success:', data);
        
        toast({
          title: "Success",
          description: isPinned ? "Post unpinned successfully!" : "Post pinned successfully!"
        });
        
        // Reload posts to reflect the change
        console.log('🔄 Reloading posts...');
        if (selectedChannel.id === 'all') {
          await loadAllPosts();
        } else {
          await loadChannelPosts(selectedChannel.id);
        }
        console.log('✅ Posts reloaded');
      } else {
        const error = await response.json();
        console.error('❌ Pin failed:', error);
        toast({
          title: "Error",
          description: error.detail || "Failed to pin/unpin post",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('❌ Error pinning/unpinning post:', error);
      toast({
        title: "Error",
        description: "Failed to pin/unpin post",
        variant: "destructive"
      });
    }
  };

  // Channel Management Functions (Admin Only)
  const openChannelModal = (channel = null) => {
    if (channel) {
      // Edit existing channel
      setEditingChannel(channel);
      setChannelForm({
        name: channel.name,
        description: channel.description,
        emoji: channel.emoji || '💬',
        channel_type: channel.channel_type || 'general',
        access_level: channel.access_level || 'free',
        is_read_only: channel.is_read_only || false
      });
    } else {
      // Create new channel
      setEditingChannel(null);
      setChannelForm({
        name: '',
        description: '',
        emoji: '💬',
        channel_type: 'general',
        access_level: 'free',
        is_read_only: false
      });
    }
    setShowChannelModal(true);
  };

  const closeChannelModal = () => {
    setShowChannelModal(false);
    setEditingChannel(null);
    setChannelForm({
      name: '',
      description: '',
      emoji: '💬',
      channel_type: 'general',
      access_level: 'free',
      is_read_only: false
    });
  };

  const handleSaveChannel = async () => {
    if (!channelForm.name.trim() || !channelForm.description.trim()) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Get admin token for admin endpoints
      const adminToken = localStorage.getItem('admin_token') || token;
      
      if (editingChannel) {
        // Update existing channel
        const response = await fetch(`${API_BASE_URL}/api/admin/community/channels/${editingChannel.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(channelForm)
        });

        if (response.ok) {
          toast({
            title: "Success",
            description: "Channel updated successfully!"
          });
          await loadCommunityChannels();
          closeChannelModal();
        } else {
          const error = await response.json();
          toast({
            title: "Error",
            description: error.detail || "Failed to update channel",
            variant: "destructive"
          });
        }
      } else {
        // Create new channel
        const response = await fetch(`${API_BASE_URL}/api/admin/community/channels`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(channelForm)
        });

        if (response.ok) {
          toast({
            title: "Success",
            description: "Channel created successfully!"
          });
          await loadCommunityChannels();
          closeChannelModal();
        } else {
          const error = await response.json();
          toast({
            title: "Error",
            description: error.detail || "Failed to create channel",
            variant: "destructive"
          });
        }
      }
    } catch (error) {
      console.error('Error saving channel:', error);
      toast({
        title: "Error",
        description: "Failed to save channel",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteChannel = async (channelId, channelName) => {
    if (!window.confirm(`Are you sure you want to delete "${channelName}"? This will also delete all posts in this channel.`)) {
      return;
    }

    try {
      // Get admin token for admin endpoints
      const adminToken = localStorage.getItem('admin_token') || token;
      
      const response = await fetch(`${API_BASE_URL}/api/admin/community/channels/${channelId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Channel deleted successfully!"
        });
        
        // If we're viewing the deleted channel, switch to "All Posts"
        if (selectedChannel?.id === channelId) {
          const allChannel = channels.find(c => c.id === 'all');
          setSelectedChannel(allChannel);
        }
        
        await loadCommunityChannels();
      } else {
        const error = await response.json();
        toast({
          title: "Error",
          description: error.detail || "Failed to delete channel",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error deleting channel:', error);
      toast({
        title: "Error",
        description: "Failed to delete channel",
        variant: "destructive"
      });
    }
  };

  // Search functionality
  const handleSearch = async (query) => {
    setSearchQuery(query);
    
    if (!query || query.trim().length < 2) {
      setShowSearchResults(false);
      setSearchResults([]);
      return;
    }

    try {
      setIsSearching(true);
      const response = await fetch(`${API_BASE_URL}/api/community/search?q=${encodeURIComponent(query)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.posts || []);
        setShowSearchResults(true);
      } else {
        console.error('Search failed');
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Error searching posts:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
  };

  // Channel reordering functions
  const moveChannelUp = async (channelIndex) => {
    if (channelIndex <= 1) return; // Can't move "All Posts" or first real channel up
    
    const newChannels = [...channels];
    // Swap with previous channel
    [newChannels[channelIndex], newChannels[channelIndex - 1]] = [newChannels[channelIndex - 1], newChannels[channelIndex]];
    
    // Update local state immediately for smooth UX
    setChannels(newChannels);
    
    // Send update to backend
    await updateChannelOrder(newChannels);
  };

  const moveChannelDown = async (channelIndex) => {
    if (channelIndex === 0 || channelIndex >= channels.length - 1) return; // Can't move "All Posts" or last channel down
    
    const newChannels = [...channels];
    // Swap with next channel
    [newChannels[channelIndex], newChannels[channelIndex + 1]] = [newChannels[channelIndex + 1], newChannels[channelIndex]];
    
    // Update local state immediately for smooth UX
    setChannels(newChannels);
    
    // Send update to backend
    await updateChannelOrder(newChannels);
  };

  const updateChannelOrder = async (orderedChannels) => {
    try {
      // Get admin token
      const adminToken = localStorage.getItem('admin_token') || token;
      
      // Build order map (skip "All Posts" channel)
      const orderMap = {};
      orderedChannels.forEach((channel, index) => {
        if (channel.id !== 'all') {
          orderMap[channel.id] = index;
        }
      });

      const response = await fetch(`${API_BASE_URL}/api/admin/community/channels/reorder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderMap)
      });

      if (!response.ok) {
        // Revert on error
        await loadCommunityChannels();
        toast({
          title: "Error",
          description: "Failed to reorder channels",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error updating channel order:', error);
      // Revert on error
      await loadCommunityChannels();
      toast({
        title: "Error",
        description: "Failed to reorder channels",
        variant: "destructive"
      });
    }
  };

  const handleReaction = async (postId, reactionType, isComment = false) => {
    try {
      const url = isComment
        ? `${API_BASE_URL}/api/community/comments/${postId}/reactions`
        : `${API_BASE_URL}/api/community/posts/${postId}/reactions`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reaction_type: reactionType })
      });

      if (response.ok) {
        if (selectedChannel.id === 'all') {
          loadAllPosts();
        } else {
          loadChannelPosts(selectedChannel.id);
        }
      }
    } catch (error) {
      console.error('Error adding reaction:', error);
    }
  };

  const toggleComments = async (postId) => {
    const isExpanding = !expandedComments[postId];
    
    setExpandedComments(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));

    // Fetch comments if expanding and not already loaded
    if (isExpanding && !comments[postId]) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/community/posts/${postId}/comments`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          setComments(prev => ({
            ...prev,
            [postId]: data.comments || []
          }));
        }
      } catch (error) {
        console.error('Error fetching comments:', error);
      }
    }
  };

  const handleAddComment = async (postId) => {
    const content = newComment[postId];
    if (!content || !content.trim()) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/community/posts/${postId}/comments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          post_id: postId,
          content: content
        })
      });

      if (response.ok) {
        setNewComment(prev => ({ ...prev, [postId]: '' }));
        // Reload comments for this post
        const commentsResponse = await fetch(`${API_BASE_URL}/api/community/posts/${postId}/comments`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (commentsResponse.ok) {
          const data = await commentsResponse.json();
          setComments(prev => ({
            ...prev,
            [postId]: data.comments || []
          }));
        }
        if (selectedChannel.id === 'all') {
          loadAllPosts();
        } else {
          loadChannelPosts(selectedChannel.id);
        }
      } else {
        const error = await response.json();
        toast({
          title: "Error",
          description: error.detail || "Failed to add comment",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/community/comments/${commentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Comment deleted successfully!"
        });
        if (selectedChannel.id === 'all') {
          loadAllPosts();
        } else {
          loadChannelPosts(selectedChannel.id);
        }
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const getReactionCounts = (reactions) => {
    if (!reactions) return {};
    const counts = {};
    Object.entries(reactions).forEach(([type, users]) => {
      if (users && users.length > 0) {
        counts[type] = users.length;
      }
    });
    return counts;
  };

  const hasUserReacted = (reactions, userId, reactionType) => {
    if (!reactions || !reactions[reactionType]) return false;
    return reactions[reactionType].includes(userId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-rose-500 border-t-transparent mb-4"></div>
          <p className="text-gray-600">Loading community...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-purple-50 flex flex-col md:flex-row pb-20 md:pb-0 overflow-hidden" style={{paddingTop: '64px'}}>
      {/* Left Sidebar - Channels (Desktop) - Starts from header */}
      <div className="hidden md:flex md:w-64 bg-white border-r border-gray-200 flex-shrink-0 flex-col md:fixed md:top-20 md:left-0" style={{height: 'calc(100vh - 80px)'}}>
        {/* Header */}
        <div className="px-4 py-1.5 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigate('/app')}
              className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-serif font-bold text-gray-900">Community</h1>
              <p className="text-xs text-gray-600">Connect & share</p>
            </div>
          </div>
        </div>

        {/* Community Guidelines Button - At Top for Visibility */}
        <div className="p-2 border-b border-gray-200 flex-shrink-0 bg-rose-50">
          <button
            onClick={() => setShowRulesModal(true)}
            className="w-full text-left px-4 py-2 rounded-lg bg-gradient-to-r from-rose-100 to-pink-100 hover:from-rose-200 hover:to-pink-200 text-rose-700 transition-all shadow-sm"
          >
            <div className="flex items-center space-x-2">
              <span className="text-lg">📜</span>
              <span className="font-medium text-xs">Community Guidelines</span>
            </div>
          </button>
        </div>

        {/* Channel List - Scrollable if needed */}
        <div className="p-2 overflow-y-auto" style={{flex: '1 1 0', minHeight: 0}}>
          {/* Add New Channel Button (Admin Only) */}
          {isAdmin && (
            <button
              onClick={() => openChannelModal()}
              className="w-full px-4 py-2 mb-3 rounded-lg border-2 border-dashed border-rose-300 hover:border-rose-500 hover:bg-rose-50 text-rose-600 transition-all flex items-center justify-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span className="font-medium text-sm">Add New Channel</span>
            </button>
          )}
          
          {channels.map((channel, index) => (
            <div
              key={channel.id}
              className={`relative group mb-1 rounded-lg ${
                selectedChannel?.id === channel.id
                  ? 'bg-gradient-to-r from-rose-500 to-pink-600 text-white shadow-md'
                  : 'hover:bg-gray-100'
              }`}
            >
              <button
                onClick={() => setSelectedChannel(channel)}
                className={`w-full text-left px-4 py-2 rounded-lg transition-all ${
                  selectedChannel?.id === channel.id
                    ? 'text-white'
                    : 'text-gray-700'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <span className="text-xl">{channel.emoji}</span>
                  <span className="font-medium text-sm flex-1">{channel.name}</span>
                  {channel.access_level === 'paid' && (
                    <Lock className="w-3.5 h-3.5 text-amber-500" title="Growth plan required" />
                  )}
                </div>
              </button>
              
              {/* Admin Actions - Reorder/Edit/Delete (Only for real channels, not "All") */}
              {isAdmin && channel.id !== 'all' && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity flex items-center space-x-1">
                  {/* Reorder buttons */}
                  <div className="flex flex-col -space-y-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        moveChannelUp(index);
                      }}
                      disabled={index <= 1}
                      className={`p-0.5 rounded hover:bg-white/20 transition-all ${
                        index <= 1 ? 'opacity-30 cursor-not-allowed' : ''
                      } ${selectedChannel?.id === channel.id ? 'text-white' : 'text-gray-600'}`}
                      title="Move up"
                    >
                      <ChevronUp className="w-3 h-3" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        moveChannelDown(index);
                      }}
                      disabled={index >= channels.length - 1}
                      className={`p-0.5 rounded hover:bg-white/20 transition-all ${
                        index >= channels.length - 1 ? 'opacity-30 cursor-not-allowed' : ''
                      } ${selectedChannel?.id === channel.id ? 'text-white' : 'text-gray-600'}`}
                      title="Move down"
                    >
                      <ChevronDownIcon className="w-3 h-3" />
                    </button>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openChannelModal(channel);
                    }}
                    className={`p-1 rounded hover:bg-white/20 transition-all ${
                      selectedChannel?.id === channel.id ? 'text-white' : 'text-gray-600'
                    }`}
                    title="Edit channel"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteChannel(channel.id, channel.name);
                    }}
                    className={`p-1 rounded hover:bg-white/20 transition-all ${
                      selectedChannel?.id === channel.id ? 'text-white' : 'text-red-600'
                    }`}
                    title="Delete channel"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Main Content Area - Only this section scrolls */}
      <div className="flex-1 px-3 md:px-4 md:pl-[272px] pb-4">
        {/* Mobile Header */}
        <div className="md:hidden mb-2">
          <div className="flex items-center space-x-2 mb-2">
            <button
              onClick={() => navigate('/app')}
              className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl font-serif font-bold text-gray-900">Community</h1>
              <p className="text-xs text-gray-600">Connect & share</p>
            </div>
            <button
              onClick={() => setShowRulesModal(true)}
              className="px-3 py-1.5 rounded-full bg-rose-50 hover:bg-rose-100 text-rose-700 transition-all text-xs font-medium"
            >
              📜 Guidelines
            </button>
          </div>
          
          {/* Mobile Channel Selector */}
          <div className="mb-4">
            <select
              value={selectedChannel?.id || ''}
              onChange={(e) => {
                const channel = channels.find(c => c.id === e.target.value);
                if (channel) setSelectedChannel(channel);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 font-medium"
            >
              {!selectedChannel && <option value="">Select a channel...</option>}
              {channels.map((channel) => (
                <option key={channel.id} value={channel.id}>
                  {channel.emoji} {channel.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="max-w-3xl mx-auto">
          {/* Search Bar */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search posts by keywords..."
                className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-300"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
            {isSearching && (
              <p className="text-sm text-gray-500 mt-2">Searching...</p>
            )}
            {showSearchResults && (
              <p className="text-sm text-gray-600 mt-2">
                Found {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{searchQuery}"
              </p>
            )}
          </div>

          {/* Selected Channel Header */}
          {selectedChannel && !showSearchResults && (
            <Card className="mb-4 p-6 bg-white/90 border-rose-200">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1">
                  <div className="text-4xl">{selectedChannel.emoji}</div>
                  <div className="flex-1">
                    <h2 className="font-serif font-bold text-2xl text-gray-900">{selectedChannel.name}</h2>
                    <p className="text-sm text-gray-600 mt-1">{selectedChannel.description}</p>
                  </div>
                </div>
                {(!selectedChannel.is_read_only || isAdmin) && (
                  <Button
                    onClick={() => {
                      setShowNewPostModal(true);
                      if (selectedChannel.id !== 'all') {
                        setSelectedPostChannel(selectedChannel.id);
                      }
                    }}
                    className="bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white rounded-full"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    New Post
                  </Button>
                )}
              </div>
            </Card>
          )}

          {/* Posts */}
          <div className="space-y-4">
            {(showSearchResults ? searchResults : posts).length === 0 ? (
              <Card className="p-8 bg-white/90 border-rose-200 text-center">
                <MessageCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">
                  {showSearchResults 
                    ? `No results found for "${searchQuery}"`
                    : 'No posts yet. Be the first to share!'}
                </p>
              </Card>
            ) : (
              (showSearchResults ? searchResults : posts).map((post) => (
                <Card key={post.id} className={`p-6 bg-white/90 ${post.is_pinned ? 'border-2 border-rose-400 shadow-md' : 'border-rose-200'}`}>
                  {/* Pinned Badge */}
                  {post.is_pinned && (
                    <div className="mb-3">
                      <span className="inline-flex items-center space-x-1 px-3 py-1 bg-gradient-to-r from-rose-500 to-pink-600 text-white text-xs font-bold rounded-full">
                        <Pin className="w-3 h-3" />
                        <span>PINNED</span>
                      </span>
                    </div>
                  )}
                  
                  {/* Show channel badge if viewing "All" */}
                  {selectedChannel?.id === 'all' && (
                    <div className="mb-3">
                      <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full">
                        {post.channel_emoji} {post.channel_name}
                      </span>
                    </div>
                  )}
                  
                  {/* Post Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      {/* User avatar */}
                      <div className="flex-shrink-0">
                        {post.user_profile_picture ? (
                          <img 
                            src={
                              // Handle both base64 images (data:image/...) and URL-based images
                              post.user_profile_picture.startsWith('data:') 
                                ? post.user_profile_picture 
                                : `${API_BASE_URL}${post.user_profile_picture}`
                            }
                            alt={post.user_name}
                            className="w-10 h-10 rounded-full object-cover"
                            onError={(e) => {
                              // Fallback to initials if image fails to load
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div 
                          className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                            post.user_type === 'admin' 
                              ? 'bg-gradient-to-br from-purple-500 to-pink-600' 
                              : 'bg-gradient-to-br from-rose-400 to-pink-500'
                          }`}
                          style={{ display: post.user_profile_picture ? 'none' : 'flex' }}
                        >
                          {post.user_name.charAt(0).toUpperCase()}
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <p className="font-medium text-gray-900">{post.user_name}</p>
                          {post.user_type === 'admin' && (
                            <span className="px-2 py-0.5 bg-gradient-to-r from-purple-500 to-pink-600 text-white text-xs font-bold rounded-full">
                              ADMIN
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">
                          {new Date(post.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    {(post.user_id === user?.id || isAdmin) && (
                      <div className="relative">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenPostMenu(openPostMenu === post.id ? null : post.id);
                          }}
                          className="p-2 hover:bg-gray-100 rounded-full"
                        >
                          <MoreVertical className="w-4 h-4 text-gray-600" />
                        </button>
                        {openPostMenu === post.id && (
                          <>
                            {/* Backdrop to close menu when clicking outside */}
                            <div 
                              className="fixed inset-0 z-10" 
                              onClick={() => setOpenPostMenu(null)}
                            />
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                              {isAdmin && (
                                <button
                                  onClick={() => {
                                    handlePinPost(post.id, post.is_pinned);
                                    setOpenPostMenu(null);
                                  }}
                                  className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-2 text-rose-600 rounded-t-lg"
                                >
                                  {post.is_pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                                  <span>{post.is_pinned ? 'Unpin' : 'Pin'}</span>
                                </button>
                              )}
                              <button
                                onClick={() => {
                                  setEditingPost(post.id);
                                  setEditContent(post.content);
                                  setOpenPostMenu(null);
                                }}
                                className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-2"
                              >
                                <Edit2 className="w-4 h-4" />
                                <span>Edit</span>
                              </button>
                              <button
                                onClick={() => {
                                  handleDeletePost(post.id);
                                  setOpenPostMenu(null);
                                }}
                                className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-2 text-red-600 rounded-b-lg"
                              >
                                <Trash2 className="w-4 h-4" />
                                <span>Delete</span>
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Post Content */}
                  {editingPost === post.id ? (
                    <div className="mb-4">
                      <textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                        rows="3"
                      />
                      <div className="flex space-x-2 mt-2">
                        <Button
                          onClick={() => handleEditPost(post.id)}
                          size="sm"
                          className="bg-rose-500 hover:bg-rose-600 text-white"
                        >
                          Save
                        </Button>
                        <Button
                          onClick={() => {
                            setEditingPost(null);
                            setEditContent('');
                          }}
                          size="sm"
                          variant="outline"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-800 mb-4 whitespace-pre-wrap">{post.content}</p>
                  )}

                  {/* Post Images and Files */}
                  {post.images && post.images.length > 0 && (
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      {post.images.map((fileUrl, idx) => {
                        // Check if it's a PDF
                        const isPDF = typeof fileUrl === 'string' && fileUrl.toLowerCase().endsWith('.pdf');
                        
                        // Build full URL - handle base64, full URLs, and relative URLs
                        let fullUrl;
                        if (typeof fileUrl === 'string') {
                          if (fileUrl.startsWith('data:')) {
                            // Base64 image - use as-is
                            fullUrl = fileUrl;
                          } else if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
                            // Full URL - use as-is
                            fullUrl = fileUrl;
                          } else {
                            // Relative URL - prefix with API_BASE_URL
                            fullUrl = `${API_BASE_URL}${fileUrl}`;
                          }
                        } else {
                          console.error('Invalid file URL type:', fileUrl);
                          return null;
                        }
                        
                        return isPDF ? (
                          <a
                            key={idx}
                            href={fullUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-red-50 border-2 border-red-200 rounded-lg p-4 hover:bg-red-100 transition-all flex flex-col items-center justify-center h-48"
                          >
                            <span className="text-5xl mb-2">📄</span>
                            <span className="text-sm text-gray-700 font-medium">View PDF</span>
                            <span className="text-xs text-gray-500 mt-1">Click to open</span>
                          </a>
                        ) : (
                          <img
                            key={idx}
                            src={fullUrl}
                            alt="Post attachment"
                            className="rounded-lg w-full h-48 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => window.open(fullUrl, '_blank')}
                            onError={(e) => {
                              console.error('Image failed to load. Details:', { 
                                originalUrl: fileUrl, 
                                fullUrl: fullUrl,
                                API_BASE_URL: API_BASE_URL 
                              });
                              e.target.style.display = 'none';
                            }}
                          />
                        );
                      })}
                    </div>
                  )}

                  {/* Reactions */}
                  <div className="flex items-center space-x-4 mb-4 border-t border-b border-gray-200 py-3">
                    {Object.entries(REACTIONS).map(([type, { emoji, label }]) => {
                      const reactionCounts = getReactionCounts(post.reactions);
                      const count = reactionCounts[type] || 0;
                      const hasReacted = hasUserReacted(post.reactions, user?.id, type);

                      return (
                        <button
                          key={type}
                          onClick={() => handleReaction(post.id, type)}
                          className={`flex items-center space-x-1 px-3 py-1 rounded-full transition-all ${
                            hasReacted
                              ? 'bg-rose-100 text-rose-600'
                              : 'hover:bg-gray-100 text-gray-600'
                          }`}
                        >
                          <span className="text-lg">{emoji}</span>
                          {count > 0 && <span className="text-sm font-medium">{count}</span>}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => toggleComments(post.id)}
                      className="flex items-center space-x-1 px-3 py-1 rounded-full hover:bg-gray-100 text-gray-600"
                    >
                      <MessageCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">{post.reply_count || 0}</span>
                    </button>
                  </div>

                  {/* Comments Section */}
                  {expandedComments[post.id] && (
                    <div className="space-y-3 mt-4">
                      {/* Add Comment */}
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          value={newComment[post.id] || ''}
                          onChange={(e) =>
                            setNewComment(prev => ({ ...prev, [post.id]: e.target.value }))
                          }
                          onKeyPress={(e) => e.key === 'Enter' && handleAddComment(post.id)}
                          placeholder="Write a comment..."
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                        />
                        <Button
                          onClick={() => handleAddComment(post.id)}
                          size="sm"
                          className="bg-rose-500 hover:bg-rose-600 text-white rounded-full"
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>

                      {/* Comments List */}
                      {comments[post.id] && comments[post.id].length > 0 && (
                        <div className="space-y-3 mt-4 pt-4 border-t border-gray-200">
                          {comments[post.id].map((comment) => (
                            <div key={comment.id} className="flex space-x-3">
                              <div className="flex-shrink-0">
                                {comment.user_profile_picture ? (
                                  <img 
                                    src={
                                      // Handle both base64 images (data:image/...) and URL-based images
                                      comment.user_profile_picture.startsWith('data:') 
                                        ? comment.user_profile_picture 
                                        : `${API_BASE_URL}${comment.user_profile_picture}`
                                    }
                                    alt={comment.user_name}
                                    className="w-8 h-8 rounded-full object-cover"
                                    onError={(e) => {
                                      // Fallback to initials if image fails to load
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                <div 
                                  className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                                    comment.user_type === 'admin'
                                      ? 'bg-gradient-to-br from-purple-500 to-pink-600'
                                      : 'bg-gradient-to-br from-rose-400 to-pink-500'
                                  }`}
                                  style={{ display: comment.user_profile_picture ? 'none' : 'flex' }}
                                >
                                  {comment.user_name.charAt(0).toUpperCase()}
                                </div>
                              </div>
                              <div className="flex-1">
                                <div className="bg-gray-50 rounded-lg p-3">
                                  <div className="flex items-center space-x-2 mb-1">
                                    <p className="font-medium text-sm text-gray-900">{comment.user_name}</p>
                                    {comment.user_type === 'admin' && (
                                      <span className="px-2 py-0.5 bg-gradient-to-r from-purple-500 to-pink-600 text-white text-xs font-bold rounded-full">
                                        ADMIN
                                      </span>
                                    )}
                                    {(comment.user_id === user?.id || isAdmin) && (
                                      <button
                                        onClick={() => handleDeleteComment(comment.id)}
                                        className="ml-auto text-red-500 hover:text-red-700 text-xs"
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </button>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-700">{comment.content}</p>
                                </div>
                                {/* Comment Reactions */}
                                <div className="flex items-center space-x-2 mt-2">
                                  {Object.entries(REACTIONS).map(([type, { emoji }]) => {
                                    const reactionCounts = getReactionCounts(comment.reactions);
                                    const count = reactionCounts[type] || 0;
                                    const hasReacted = hasUserReacted(comment.reactions, user?.id, type);

                                    return (
                                      <button
                                        key={type}
                                        onClick={() => handleReaction(comment.id, type, true)}
                                        className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs transition-all ${
                                          hasReacted
                                            ? 'bg-rose-100 text-rose-600'
                                            : 'hover:bg-gray-100 text-gray-600'
                                        }`}
                                      >
                                        <span>{emoji}</span>
                                        {count > 0 && <span className="font-medium">{count}</span>}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              ))
            )}
          </div>

          {/* New Post Modal */}
          {showNewPostModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-serif font-bold text-gray-900">Create Post</h2>
                  <button
                    onClick={() => {
                      setShowNewPostModal(false);
                      setNewPostContent('');
                      setNewPostImages([]);
                      setSelectedPostChannel(null);
                    }}
                    className="p-2 hover:bg-gray-100 rounded-full"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Channel Selector (only if posting from "All") */}
                {selectedChannel?.id === 'all' && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Channel
                    </label>
                    <div className="relative">
                      <select
                        value={selectedPostChannel || channels.find(c => c.name === '💬 General Discussions')?.id || ''}
                        onChange={(e) => setSelectedPostChannel(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg appearance-none bg-white focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                      >
                        {channels.filter(c => c.id !== 'all' && (!c.is_read_only || isAdmin)).map(channel => (
                          <option key={channel.id} value={channel.id}>
                            {channel.emoji} {channel.name} {channel.is_read_only ? ' (Admins Only)' : ''}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-4 w-5 h-5 text-gray-400 pointer-events-none" />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Defaults to General Discussions if not selected</p>
                  </div>
                )}

                <textarea
                  value={newPostContent}
                  onChange={(e) => setNewPostContent(e.target.value)}
                  placeholder="Share something with the community..."
                  className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent mb-4"
                  rows="6"
                />

                {/* File Preview (Images and PDFs) */}
                {newPostImages.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {newPostImages.map((file, idx) => {
                      // Handle both object format and string format
                      const fileType = typeof file === 'object' ? file.type : '';
                      const fileUrl = typeof file === 'object' ? file.url : file;
                      const fileName = typeof file === 'object' ? file.name : 'file';
                      const isPDF = fileType === 'application/pdf';
                      
                      return (
                        <div key={idx} className="relative">
                          {isPDF ? (
                            <div className="w-full h-32 bg-red-50 border-2 border-red-200 rounded-lg flex flex-col items-center justify-center p-2">
                              <span className="text-3xl mb-1">📄</span>
                              <span className="text-xs text-gray-600 text-center truncate w-full px-1">
                                {fileName}
                              </span>
                            </div>
                          ) : (
                            <img
                              src={fileUrl}
                              alt="Upload preview"
                              className="w-full h-32 object-cover rounded-lg"
                              onError={(e) => {
                                console.error('Image failed to load:', fileUrl);
                                e.target.style.display = 'none';
                              }}
                            />
                          )}
                          <button
                            onClick={() => removeImage(idx)}
                            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 shadow-lg"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div className="flex space-x-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageUpload}
                      accept="image/*,application/pdf,.pdf"
                      multiple
                      className="hidden"
                    />
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      variant="outline"
                      size="sm"
                      disabled={isSubmitting}
                    >
                      <ImageIcon className="w-4 h-4 mr-2" />
                      Add Files
                    </Button>
                    <span className="text-xs text-gray-500 self-center">Images & PDFs</span>
                  </div>

                  <Button
                    onClick={handleCreatePost}
                    disabled={isSubmitting || !newPostContent.trim()}
                    className="bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white"
                  >
                    {isSubmitting ? 'Posting...' : 'Post'}
                  </Button>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Community Rules & Guidelines Modal */}
      {showRulesModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white p-6">
            <div className="mb-6">
              <h2 className="text-3xl font-serif font-bold text-gray-900 mb-2 text-center">
                Community Rules & Guidelines 💕
              </h2>
              <p className="text-sm text-gray-600 text-center">
                Our sacred space for growth, support, and sisterhood
              </p>
            </div>

            <div className="space-y-6">
              {/* Core Values */}
              <div className="bg-gradient-to-r from-rose-50 to-pink-50 rounded-lg p-4 border border-rose-200">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <span className="text-xl mr-2">✨</span>
                  Our Core Values
                </h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start"><span className="mr-2">🌸</span><span><strong>Compassion First:</strong> We hold space for every woman's journey with kindness</span></li>
                  <li className="flex items-start"><span className="mr-2">💫</span><span><strong>Authenticity:</strong> Share your truth - vulnerability is strength here</span></li>
                  <li className="flex items-start"><span className="mr-2">🙏</span><span><strong>Support & Uplift:</strong> We rise by lifting each other</span></li>
                  <li className="flex items-start"><span className="mr-2">🌱</span><span><strong>Growth Mindset:</strong> Every setback is a setup for a comeback</span></li>
                </ul>
              </div>

              {/* Community Guidelines */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <span className="text-xl mr-2">📜</span>
                  Community Guidelines
                </h3>
                <div className="space-y-3 text-sm text-gray-700">
                  <div className="flex items-start">
                    <span className="font-semibold text-green-600 mr-2">✓</span>
                    <div>
                      <strong className="text-gray-900">Be Respectful & Kind</strong>
                      <p className="text-gray-600">Treat every member with dignity. Disagreements are okay, disrespect is not.</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <span className="font-semibold text-green-600 mr-2">✓</span>
                    <div>
                      <strong className="text-gray-900">Keep It Safe</strong>
                      <p className="text-gray-600">No hate speech, harassment, bullying, or discrimination of any kind.</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <span className="font-semibold text-green-600 mr-2">✓</span>
                    <div>
                      <strong className="text-gray-900">Respect Privacy</strong>
                      <p className="text-gray-600">What's shared here stays here. Don't screenshot or share personal stories outside the community.</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <span className="font-semibold text-green-600 mr-2">✓</span>
                    <div>
                      <strong className="text-gray-900">No Spam or Promotion</strong>
                      <p className="text-gray-600">This is a community, not a marketplace. No advertising, MLM, or self-promotion.</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <span className="font-semibold text-green-600 mr-2">✓</span>
                    <div>
                      <strong className="text-gray-900">Use Appropriate Content</strong>
                      <p className="text-gray-600">Keep content family-friendly. No explicit, violent, or disturbing material.</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <span className="font-semibold text-green-600 mr-2">✓</span>
                    <div>
                      <strong className="text-gray-900">Stay On Topic</strong>
                      <p className="text-gray-600">Post in the appropriate channel. Keep discussions relevant to personal growth and self-love.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* What to Avoid */}
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <span className="text-xl mr-2">🚫</span>
                  What to Avoid
                </h3>
                <ul className="space-y-1.5 text-sm text-gray-700">
                  <li className="flex items-start"><span className="mr-2">×</span><span>Negative comments about someone's appearance, journey, or choices</span></li>
                  <li className="flex items-start"><span className="mr-2">×</span><span>Unsolicited advice (ask first: "Would you like my thoughts on this?")</span></li>
                  <li className="flex items-start"><span className="mr-2">×</span><span>Politics, religion debates, or divisive topics</span></li>
                  <li className="flex items-start"><span className="mr-2">×</span><span>Medical, legal, or financial advice (suggest professionals instead)</span></li>
                  <li className="flex items-start"><span className="mr-2">×</span><span>Excessive venting without seeking support or solutions</span></li>
                </ul>
              </div>

              {/* Report & Moderation */}
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <span className="text-xl mr-2">⚖️</span>
                  Report & Moderation
                </h3>
                <p className="text-sm text-gray-700 mb-2">
                  If you see something that violates these guidelines, please report it. Our moderation team reviews all reports within 24 hours.
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Violations may result in:</strong> Warning → Temporary suspension → Permanent removal
                </p>
              </div>

              {/* Agreement */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                <p className="text-sm text-gray-700 text-center">
                  By entering this community, you agree to uphold these guidelines and contribute to a safe, supportive space for all women on their self-love journey 💕
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                onClick={() => {
                  localStorage.setItem(`community_rules_agreed_${user?.id}`, 'true');
                  setHasAgreedToRules(true);
                  setShowRulesModal(false);
                }}
                className="flex-1 bg-gradient-to-r from-rose-500 to-pink-600 text-white"
              >
                I Agree - Enter Community 💕
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Channel Create/Edit Modal (Admin Only) */}
      {showChannelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <Card className="w-full max-w-lg bg-white p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                {editingChannel ? 'Edit Channel' : 'Create New Channel'}
              </h3>
              <button
                onClick={closeChannelModal}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Channel Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Channel Name (include emoji at the start) *
                </label>
                <input
                  type="text"
                  value={channelForm.name}
                  onChange={(e) => setChannelForm({ ...channelForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-300"
                  placeholder="e.g., 💬 Daily Wins"
                  maxLength={100}
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  value={channelForm.description}
                  onChange={(e) => setChannelForm({ ...channelForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-300"
                  placeholder="What is this channel about?"
                  rows={3}
                  maxLength={500}
                  required
                />
              </div>

              {/* Access Level */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Access Level *
                </label>
                <select
                  value={channelForm.access_level}
                  onChange={(e) => setChannelForm({ ...channelForm, access_level: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-300"
                >
                  <option value="free">Free - Available to all users</option>
                  <option value="paid">Paid - Growth plan members only 👑</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Paid channels are visible but locked for free users
                </p>
              </div>

              {/* Read-Only Option */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="readOnly"
                  checked={channelForm.is_read_only}
                  onChange={(e) => setChannelForm({ ...channelForm, is_read_only: e.target.checked })}
                  className="w-4 h-4 text-rose-600 border-gray-300 rounded focus:ring-rose-500"
                />
                <label htmlFor="readOnly" className="text-sm text-gray-700">
                  Read-only (Only admins can post)
                </label>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mt-6">
              <Button
                onClick={closeChannelModal}
                variant="outline"
                className="flex-1"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveChannel}
                className="flex-1 bg-gradient-to-r from-rose-500 to-pink-600 text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : (editingChannel ? 'Update Channel' : 'Create Channel')}
              </Button>
            </div>
          </Card>
        </div>
      )}

    </div>
  );
};

export default Community;
